/**
 * Los estados vacios.
 *
 * ---------------------------------------------------------------------------
 * UN ESTADO VACIO QUE SOLO DICE "SIN RESULTADOS" ES UN CALLEJON
 *
 * "Cero tarifas" tiene ocho causas que se arreglan de formas distintas: una
 * pide firmar un documento, otra pedirle el correo nuevo al proveedor, otra
 * cambiar la fecha del formulario. Desde el navegador se ven todas igual. Y la
 * peor pareja es "cero porque todo vencio" contra "cero porque la consulta esta
 * rota": la segunda es un fallo que nadie reporta porque parece la primera.
 *
 * Por eso esta pantalla no adivina. La API manda un `diagnostico` --cuantas
 * tarifas hay en la ruta y por que cada una quedo fuera-- y aqui se pinta lo
 * que dice, con dos frases por causa: QUE PASA y QUE HACER. El orden es el de
 * la API, que ya viene por lo que hay que arreglar antes, no por cantidad.
 *
 * NO SE INVENTA NADA. Si el diagnostico no viene --una API vieja, un despliegue
 * a medias-- se pinta la frase de siempre y nada mas. Un estado vacio que
 * especula sobre el motivo es peor que uno que no lo dice: manda a corregir lo
 * que no estaba mal.
 * ---------------------------------------------------------------------------
 */

import type { CriterioResuelto, DiagnosticoVacio } from '../api';
import { el } from '../dom';
import { formatearFecha, type Idioma } from '../formato';
import type { Traductor } from '../i18n';

/** Un estado vacio: icono ninguno, titulo, explicacion y que hacer. */
function panelVacio(clase: string, hijos: readonly (Node | null)[]): HTMLElement {
  return el('section', { clase: `panel vacio ${clase}` }, hijos);
}

/**
 * Todavia no se ha buscado.
 *
 * Antes aqui no habia nada: la pantalla se quedaba con el formulario y el resto
 * en blanco, que es indistinguible de una busqueda que devolvio cero y de una
 * que fallo sin avisar.
 */
export function vacioInicial(t: Traductor): HTMLElement {
  return panelVacio('vacio-inicial', [
    el('h2', { texto: t('vacio.inicial.titulo') }),
    el('p', { clase: 'vacio-explica', texto: t('vacio.inicial.explica') }),
  ]);
}

/**
 * La salida del callejon: pedirle la tarifa al proveedor.
 *
 * Cero resultados tiene ocho causas y este boton no las arregla todas --una
 * tarifa sin firmar se firma, no se vuelve a pedir--, pero SI arregla la unica
 * que no tiene arreglo dentro de la aplicacion: que la ruta no exista en el
 * catalogo porque nadie nos ha mandado precio para ella. Hasta ahora ese estado
 * vacio terminaba en "todavia no ha llegado por correo", que es informacion
 * verdadera y ninguna accion.
 *
 * Va con la ruta cargada: el agente ya escribio POL, POD, equipo y fecha una
 * vez, y volver a pedirselos para preguntar lo mismo seria cobrarle dos veces
 * el mismo formulario.
 */
function botonPedirRfq(t: Traductor, onPedirRfq: (() => void) | undefined): Node | null {
  if (onPedirRfq === undefined) return null;
  return el('div', { clase: 'vacio-accion' }, [
    el('button', {
      clase: 'boton boton-primario boton-rfq',
      texto: t('rfq.desdeVacio'),
      attrs: { type: 'button' },
      onClick: onPedirRfq,
    }),
    el('p', { clase: 'tenue', texto: t('rfq.explicaVacio') }),
  ]);
}

/**
 * La busqueda devolvio cero.
 *
 * `diagnostico` es opcional a proposito: ver la cabecera del modulo.
 */
export function vacioSinResultados(
  t: Traductor,
  idioma: Idioma,
  criterio: CriterioResuelto,
  diagnostico: DiagnosticoVacio | undefined,
  onPedirRfq?: () => void,
): HTMLElement {
  const ruta = `${criterio.pol.nombre} (${criterio.pol.pais}) → ${criterio.pod.nombre} (${criterio.pod.pais})`;

  // Sin diagnostico, la frase de siempre y se acaba. No se especula.
  if (diagnostico === undefined) {
    return panelVacio('vacio-sin-resultados', [
      el('h2', { texto: t('vacio.sinResultados.titulo') }),
      el('p', { clase: 'vacio-explica', texto: t('busqueda.nadaEncontrado') }),
      botonPedirRfq(t, onPedirRfq),
    ]);
  }

  // La ruta no esta en el catalogo. Es distinto de que este y no sirva ninguna
  // tarifa, y lo que hay que hacer tambien es distinto: ahi no hay nada que
  // arreglar en las tarifas, falta el correo del proveedor.
  if (diagnostico.tarifas_en_la_ruta === 0) {
    return panelVacio('vacio-sin-resultados', [
      el('h2', { texto: t('vacio.rutaDesconocida.titulo') }),
      el('p', {
        clase: 'vacio-explica',
        texto: t('vacio.rutaDesconocida.explica', {
          ruta,
          modo: t(`modo.${criterio.modo}`),
        }),
      }),
      el('p', { clase: 'vacio-hacer', texto: t('vacio.rutaDesconocida.hacer') }),
      botonPedirRfq(t, onPedirRfq),
    ]);
  }

  return panelVacio('vacio-sin-resultados', [
    el('h2', { texto: t('vacio.sinResultados.titulo') }),
    el('p', {
      clase: 'vacio-explica',
      texto: t('vacio.hayTarifas', {
        n: String(diagnostico.tarifas_en_la_ruta),
        ruta,
      }),
    }),
    el(
      'ul',
      { clase: 'causas' },
      diagnostico.causas.map((c) =>
        el('li', { clase: 'causa' }, [
          el('div', { clase: 'causa-cabecera' }, [
            el('span', {
              clase: 'badge badge-atencion',
              texto: t(`vacio.causa.${c.causa}.titulo`),
            }),
            el('span', {
              clase: 'causa-cuenta',
              texto: t('vacio.nTarifas', { n: String(c.tarifas) }),
            }),
          ]),
          el('p', {
            clase: 'causa-explica',
            texto: t(`vacio.causa.${c.causa}.explica`, {
              n: String(c.tarifas),
              // Vacio en aereo, donde no hay equipo: las causas aereas no lo
              // mencionan --hablan de escalones y de peso-- asi que el hueco no
              // llega a ninguna frase.
              equipo: criterio.equipo?.codigo ?? '',
              fecha: formatearFecha(criterio.fecha_embarque, idioma),
              // Las tres causas aereas hablan del peso que se pregunto: sin el
              // numero delante, "el peso queda fuera de los escalones" no dice
              // que peso hay que cambiar.
              peso: criterio.carga?.peso_kg ?? '',
              volumen: criterio.carga?.volumen_m3 ?? '',
              // [Invariante 3] Una vigencia ausente no se pinta como un hueco:
              // se dice que no la declararon, que es justo lo que la convierte
              // en vencida.
              ultima:
                c.ultima_vigencia === null
                  ? t('vacio.sinVigencia')
                  : formatearFecha(c.ultima_vigencia, idioma),
            }),
          }),
          el('p', {
            clase: 'causa-hacer',
            texto: t(`vacio.causa.${c.causa}.hacer`),
          }),
        ]),
      ),
    ),
    botonPedirRfq(t, onPedirRfq),
  ]);
}
